import * as Safe from "safe-portals";
import { Serializers } from "@flow/common/types";
import { makeActionTypeDefinition } from "@flow/common/action_type";
import { CampaignTargetProviderConfigSerializer } from "@flow/common/campaign_target_provider";
import { thankYouEmailCustomConfig } from "@flow/core/shared/common_custom_config";

export type ActionConfig = Safe.TypeIn<typeof SendAMessageAction_def.custom_config_type>;
export type ActionCustomData = Safe.TypeIn<typeof ActionFrontendDataSerializer>;
export type ContactMethod = "email" | "video";
/**
 * This data is passed to ../frontend/member/SendAMessageActionComponent when this action is begun
 * the data is generated in ../backend/action_type_handlers
 */
const ActionFrontendDataSerializer = Safe.obj({
  title: Safe.str,
  action_blurb: Safe.str,
  landing_blurb: Safe.str,
  action_info2: Safe.str,
  media_info: Safe.str,
  presubmit_video_text: Safe.str,
  decision_target: Safe.optional(CampaignTargetProviderConfigSerializer),
  consent_configs: Safe.obj({
    common: Safe.array(Serializers.consentConfig),
    video: Safe.array(Serializers.consentConfig),
  }),
  video_email_introduction_template: Safe.str,
  enabled_contact_methods: Safe.obj<Record<ContactMethod, typeof Safe.bool>>({
    video: Safe.bool,
    email: Safe.bool,
  }),
  reminder_email: Safe.obj({
    enabled: Safe.bool,
    number_of_days: Safe.int,
    subject: Safe.str,
    body: Safe.str,
    footer: Safe.str,
  }),
  sam_prefill: Safe.obj({
    enabled: Safe.bool,
    subject: Safe.str,
    body: Safe.str,
  }),
});

export const SendAMessageAction_def = makeActionTypeDefinition({
  plugin: "send_a_message",
  // action type identifier. don't change this without migrating data
  action_type: "send_a_message:SendAMessageAction",
  supports_a_b_tests: false,
  custom_config_type: Safe.obj({
    // here you add action properties that can be edited when creating a journey
    // they are passed to ../frontend/admin/EditSendAMessageAction
    title: Safe.str,
    action_blurb: Safe.str,
    landing_blurb: Safe.str,
    action_info2: Safe.str,
    media_info: Safe.str,
    decision_target: Safe.optional(CampaignTargetProviderConfigSerializer),
    presubmit_video_text: Safe.str,
    email_footer: Safe.str,
    video_email_introduction_template: Safe.str,
    enabled_contact_methods: Safe.obj({
      video: Safe.bool,
      email: Safe.bool,
    }),
    moderate_contact_methods: Safe.obj({
      video: Safe.bool,
      email: Safe.bool,
    }),
    ...thankYouEmailCustomConfig,
    reminder_email: Safe.obj({
      enabled: Safe.bool,
      number_of_days: Safe.int,
      subject: Safe.str,
      body: Safe.str,
      footer: Safe.str,
    }),
    sam_prefill: Safe.optional(
      Safe.obj({
        enabled: Safe.bool,
        subject: Safe.str,
        body: Safe.str,
      })
    ),
  }),
  // these are the default action properties when a new SendAMessageAction is created
  custom_config_defaults: {
    title: "Title for Send A Message",
    landing_blurb: "Will you send an email to your MP today?",
    action_blurb: "Thanks for emailing XYZ",
    action_info2:
      "<li>Explain in your own words why the issue is so important to you</li><br><li>Personal stories go a long way! How have you or people you know been affected?</li><br><li>Remember to be polite!</li><br>",
    media_info:
      "If you choose to voluntarily provide information about your political or philosophical beliefs, health, ethnicity, or any other sensitive information when send an email through the 38 Degrees site, you are consenting to 38 Degrees using this information to help plan and improve campaigns, contact you about campaigns that we think are relevant to you, understand our supporters better, and have an impact on decision makers.",
    decision_target: undefined,
    presubmit_video_text: "",
    email_footer:
      "This email was sent by a constituent of yours through 38 Degrees. If you are an MP, or work for an MP, and would like to know more about this campaign, or 38 Degrees, please email publicaffairs@38degrees.org.uk.",
    video_email_introduction_template: "",
    enabled_contact_methods: {
      video: false,
      email: true,
    },
    moderate_contact_methods: {
      video: false,
      email: false,
    },
    thank_you_email_subject: "Thanks for sending a message - one more thing",
    thank_you_email_body: `<p>If you get a reply, please will you share it with the 38 Degrees team via this simple online form: {response_url}. That way, together, we can hold decision makers to any commitments they make.</p>`,
    thank_you_email_footer: "",
    from_name: "", // There is an override in EditSendAMessageAction that uses the current user's name
    reminder_email: {
      enabled: false,
      number_of_days: 4,
      subject: "Thank you for taking an action a few days ago",
      body: "This is an automatic email to remind you...",
      footer: "",
    },
    sam_prefill: {
      enabled: false,
      subject: "some subject",
      body: `Dear MP Jane Doe,<br />
      I think.......
      `,
    },
  },
  // this data is passed to ../frontend/member/SendAMessageAction when this action is begun
  // it is generated in ../backend/action_type_handlers
  custom_data_type: ActionFrontendDataSerializer,
  check_valid: (config) => {
    const errors: Record<string, string> = {};

    // Send a message config validation
    if (config.title.length === 0) {
      errors.title = "Title is required";
    }
    if (config.decision_target === undefined) {
      errors.decision_target = "Decision maker is required";
    }
    if (config.email_footer.length === 0) {
      errors.email_footer = "Email footer is required";
    }

    // Thank you email validation
    if (config.from_name.length === 0) {
      errors.from_name = "Thank you email from name is required";
    }
    if (config.thank_you_email_subject.length === 0) {
      errors.thank_you_email_subject = "Thank you email subject is required";
    }

    return { valid: Object.keys(errors).length === 0, errors: errors };
  },
});
